import React, { useState, useRef, useEffect } from 'react';
import {
  buildFacebookVideoEmbedUrl,
  getFacebookEmbedPlayerHeight,
  isFacebookVideoPageUrl,
  normalizeFacebookVideoPageUrl,
} from '../utils/facebookVideoEmbed';

// Resizable Image Component – skalowanie bez skoków (stała „linijka” podczas przeciągania)
function ResizableImage({ src, alt, width, onWidthChange }) {
  const [isResizing, setIsResizing] = useState(false);
  const [currentWidth, setCurrentWidth] = useState(width || '100%');
  const [imageError, setImageError] = useState(false);
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const lastWidthRef = useRef(width || '100%');
  const dragStateRef = useRef({ initialWidthPx: 0, initialX: 0, containerWidthPx: 0 });

  useEffect(() => {
    const newWidth = width || '100%';
    setCurrentWidth(newWidth);
    lastWidthRef.current = newWidth;
  }, [width]);

  useEffect(() => {
    setImageError(false);
  }, [src]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const { initialWidthPx, initialX, containerWidthPx } = dragStateRef.current;
      const deltaX = e.clientX - initialX;
      let newWidthPx = initialWidthPx + deltaX;
      if (newWidthPx < 50) newWidthPx = 50;
      if (containerWidthPx > 0 && newWidthPx >= containerWidthPx * 0.98) {
        newWidthPx = containerWidthPx;
      }
      const newWidth = `${Math.round(newWidthPx)}px`;
      lastWidthRef.current = newWidth;
      setCurrentWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      const final = lastWidthRef.current;
      if (!onWidthChange) return;
      // Użyj początkowej szerokości kontenera (z mousedown), nie aktualnej – kontener się kurczy podczas dragu
      const containerWidthPx = dragStateRef.current.containerWidthPx || 0;
      if (containerWidthPx > 0 && final.endsWith('px')) {
        const px = parseFloat(final);
        if (px >= containerWidthPx * 0.95) {
          onWidthChange('100%');
          return;
        }
      }
      onWidthChange(final);
    };

    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, onWidthChange]);

  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!containerRef.current || !imageRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidthPx = containerRect.width;

    let currentWidthPx;
    if (currentWidth === '100%') {
      currentWidthPx = containerWidthPx;
    } else if (typeof currentWidth === 'string' && currentWidth.includes('%')) {
      currentWidthPx = (containerWidthPx * parseFloat(currentWidth)) / 100;
    } else {
      currentWidthPx = parseFloat(currentWidth) || containerWidthPx;
    }

    dragStateRef.current = {
      initialWidthPx: currentWidthPx,
      initialX: e.clientX,
      containerWidthPx,
    };
    setIsResizing(true);
  };

  const widthStyle = currentWidth === '100%' ? '100%' : currentWidth;

  return (
    <div 
      ref={containerRef}
      style={{ 
        position: 'relative', 
        display: 'block', 
        margin: '8px 0', 
        width: '100%',
        cursor: isResizing ? 'ew-resize' : 'default'
      }}
    >
      <img 
        ref={imageRef}
        src={src}
        alt={alt}
        style={{ 
          maxWidth: widthStyle === '100%' ? '100%' : widthStyle,
          width: widthStyle === '100%' ? '100%' : widthStyle,
          height: 'auto',
          display: 'block',
          border: '1px solid #ddd',
          borderRadius: '4px',
          margin: '0 auto',
          userSelect: 'none'
        }}
        onLoad={() => {
          setImageError(false);
        }}
        onError={() => {
          setImageError(true);
        }}
        draggable={false}
      />
      {imageError && (
        <div style={{ padding: '8px', border: '1px solid #ffc107', backgroundColor: '#fff3cd', borderRadius: '4px', fontSize: '12px', marginTop: '4px' }}>
          Błąd ładowania obrazu: {src ? (src.length > 50 ? src.substring(0, 50) + '...' : src) : 'Brak URL'}
        </div>
      )}
      <div
        onMouseDown={handleResizeStart}
        style={{
          position: 'absolute',
          bottom: '4px',
          right: '4px',
          width: '20px',
          height: '20px',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          border: '2px solid #fff',
          borderRadius: '4px',
          cursor: 'nwse-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10
        }}
        title="Przeciągnij, aby zmienić rozmiar"
      >
        <div style={{
          width: '0',
          height: '0',
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderBottom: '6px solid #fff',
          transform: 'rotate(45deg)',
          marginBottom: '2px'
        }} />
      </div>
    </div>
  );
}

// Token separatora w treści bloku: [divider]
const DIVIDER_TOKEN = '[divider]';
const SEGMENT_TOKEN_RE = /(\[image:https?:\/\/[^|\]]+(?:\|[^\]]*)?\]|\[divider\])/g;

function parseImageToken(token) {
  const m = token.match(/^\[image:(https?:\/\/[^|\]]+)(?:\|([^\]]*))?\]$/);
  if (!m) return null;
  const url = m[1];
  const params = m[2] || '';
  const seg = { type: 'image', url, width: '100%', align: 'left' };
  params.split('|').forEach(p => {
    const eq = p.indexOf('=');
    if (eq > 0) {
      const k = p.slice(0, eq).trim();
      const v = p.slice(eq + 1).trim();
      if (k === 'width' && v) seg.width = v;
      if (k === 'align' && v) seg.align = v;
    }
  });
  return seg;
}

function imageSegmentToToken(seg) {
  const { url, width, align } = seg;
  if ((!width || width === '100%') && (!align || align === 'left')) return `[image:${url}]`;
  const parts = [];
  if (width && width !== '100%') parts.push(`width=${width}`);
  if (align && align !== 'left') parts.push(`align=${align}`);
  return parts.length ? `[image:${url}|${parts.join('|')}]` : `[image:${url}]`;
}

// Dzieli tekst bloku na segmenty: tekst, [image:url...] i [divider]
function getSegments(text) {
  if (!text) return [{ type: 'text', content: '' }];
  const parts = text.split(SEGMENT_TOKEN_RE);
  const segments = [];
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (part === DIVIDER_TOKEN) {
      segments.push({ type: 'divider' });
    } else {
      const parsed = parseImageToken(part);
      if (parsed) segments.push(parsed);
      else segments.push({ type: 'text', content: part });
    }
  }
  return segments;
}

function segmentToText(seg) {
  if (seg.type === 'text') return seg.content;
  if (seg.type === 'divider') return DIVIDER_TOKEN;
  return imageSegmentToToken(seg);
}

function segmentsToText(segments) {
  return segments.map(segmentToText).join('');
}

// Jedna linia w polu = jeden blok (Enter = nowy, niezależny wiersz).
const BLOCK_DELIMITER = '\n';

function serializeOneBlock(b) {
  if (!b) return '';
  if (b.type === 'text') return (b.text || '').replace(/\r?\n/g, ' '); // w jednym bloku brak wewnętrznych \n
  if (b.type === 'divider') return '[---]';
  if (b.type === 'image') return '[BLOCK_IMAGE]\n' + (b.url || '') + '\n[/BLOCK_IMAGE]';
  if (b.type === 'link') return '[BLOCK_LINK]\n' + (b.url || '') + '\n' + (b.text || '') + '\n[/BLOCK_LINK]';
  if (b.type === 'facebookVideo') return '[BLOCK_FACEBOOK_VIDEO]\n' + (b.url || '') + '\n[/BLOCK_FACEBOOK_VIDEO]';
  return '';
}

function serializeBlocksToContent(blocks) {
  if (!blocks || blocks.length === 0) return '';
  return blocks.map(serializeOneBlock).join(BLOCK_DELIMITER);
}

function getBlockIndexFromCursor(blocks, cursor) {
  if (!blocks || blocks.length === 0) return 0;
  let pos = 0;
  for (let i = 0; i < blocks.length; i++) {
    const len = serializeOneBlock(blocks[i]).length;
    if (cursor >= pos && cursor < pos + len) return i;
    pos += len + BLOCK_DELIMITER.length;
  }
  return Math.max(0, blocks.length - 1);
}

function getBlockAndOffsetFromCursor(blocks, cursor) {
  if (!blocks || blocks.length === 0) return { blockIndex: 0, offsetInBlock: 0 };
  let pos = 0;
  for (let i = 0; i < blocks.length; i++) {
    const len = serializeOneBlock(blocks[i]).length;
    const delen = BLOCK_DELIMITER.length;
    if (cursor >= pos && cursor <= pos + len) {
      return { blockIndex: i, offsetInBlock: Math.min(cursor - pos, len) };
    }
    pos += len + delen;
  }
  const last = blocks.length - 1;
  return { blockIndex: last, offsetInBlock: serializeOneBlock(blocks[last]).length };
}

function parseContentToBlocks(content) {
  if (content == null || content === '') return [{ type: 'text', text: '', align: 'left', bold: false, italic: false, underline: false }];
  const lines = content.split(/\r?\n/);
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line === '[---]') {
      blocks.push({ type: 'divider' });
      i += 1;
    } else if (line === '[BLOCK_IMAGE]' && lines[i + 2] === '[/BLOCK_IMAGE]') {
      blocks.push({ type: 'image', url: (lines[i + 1] || '').trim() });
      i += 3;
    } else if (line === '[BLOCK_LINK]' && lines[i + 3] === '[/BLOCK_LINK]') {
      blocks.push({ type: 'link', url: (lines[i + 1] || '').trim(), text: (lines[i + 2] || '').trim() });
      i += 4;
    } else if (line === '[BLOCK_FACEBOOK_VIDEO]' && lines[i + 2] === '[/BLOCK_FACEBOOK_VIDEO]') {
      const raw = (lines[i + 1] || '').trim();
      const url = normalizeFacebookVideoPageUrl(raw) || raw;
      blocks.push({ type: 'facebookVideo', url });
      i += 3;
    } else {
      blocks.push({ type: 'text', text: line || '', align: 'left', bold: false, italic: false, underline: false });
      i += 1;
    }
  }
  if (blocks.length === 0) return [{ type: 'text', text: '', align: 'left', bold: false, italic: false, underline: false }];
  return blocks;
}

// Block-based Editor Component
export default function BlockEditor({ blocks, onBlocksChange, onImageUpload }) {
  const [cursorPosition, setCursorPosition] = useState(0);
  const [linkUrlPrompt, setLinkUrlPrompt] = useState('');
  const [linkTextPrompt, setLinkTextPrompt] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showFacebookVideoModal, setShowFacebookVideoModal] = useState(false);
  const [facebookVideoPrompt, setFacebookVideoPrompt] = useState('');
  const [facebookVideoError, setFacebookVideoError] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Validate that onBlocksChange is provided
  if (!onBlocksChange || typeof onBlocksChange !== 'function') {
    console.error('BlockEditor: onBlocksChange must be a function');
    return <div>Error: onBlocksChange is required</div>;
  }

  const updateBlock = (index, updates) => {
    if (!onBlocksChange) {
      console.error('updateBlock: onBlocksChange is not defined');
      return;
    }
    const newBlocks = [...blocks];
    // Ensure formatting properties have defaults
    const currentBlock = newBlocks[index];
    if (currentBlock && currentBlock.type === 'text') {
      newBlocks[index] = { 
        ...currentBlock,
        bold: currentBlock.bold ?? false,
        italic: currentBlock.italic ?? false,
        underline: currentBlock.underline ?? false,
        ...updates 
      };
    } else {
      newBlocks[index] = { ...currentBlock, ...updates };
    }
    onBlocksChange(newBlocks);
  };

  const validateUrl = (url) => {
    if (!url) return false;
    return url.startsWith('http://') || url.startsWith('https://');
  };

  const buildLinkToken = (url, displayText) => {
    const safeText = (displayText || '').trim().replace(/\]/g, '');
    return safeText ? `[link:${url}|${safeText}]` : `[link:${url}]`;
  };

  const insertLinkInBlock = (blockIndex, positionInBlock, url, displayText) => {
    const token = buildLinkToken(url, displayText);
    const block = blocks[blockIndex];
    if (!block || block.type !== 'text') {
      onBlocksChange([...(blocks.length ? blocks : []), { type: 'text', text: token, align: 'left', bold: false, italic: false, underline: false }]);
      return;
    }
    const text = block.text || '';
    const newText = text.slice(0, positionInBlock) + token + text.slice(positionInBlock);
    updateBlock(blockIndex, { text: newText });
  };

  const insertImageInBlock = (blockIndex, positionInBlock, url) => {
    const block = blocks[blockIndex];
    if (!block || block.type !== 'text') {
      onBlocksChange([...(blocks.length ? blocks : []), { type: 'text', text: `[image:${url}]`, align: 'left', bold: false, italic: false, underline: false }]);
      return;
    }
    const text = block.text || '';
    const newText = text.slice(0, positionInBlock) + `[image:${url}]` + text.slice(positionInBlock);
    updateBlock(blockIndex, { text: newText });
  };

  const insertSeparatorInBlock = (blockIndex, positionInBlock) => {
    const block = blocks[blockIndex];
    if (!block || block.type !== 'text') {
      onBlocksChange([...(blocks.length ? blocks : []), { type: 'text', text: DIVIDER_TOKEN, align: 'left', bold: false, italic: false, underline: false }]);
      return;
    }
    const text = block.text || '';
    const newText = text.slice(0, positionInBlock) + DIVIDER_TOKEN + text.slice(positionInBlock);
    updateBlock(blockIndex, { text: newText });
  };

  const handleInsertLinkIntoFocusedBlock = (url, displayText) => {
    const token = buildLinkToken(url, displayText);
    const { blockIndex, offsetInBlock } = getBlockAndOffsetFromCursor(blocks, cursorPosition);
    if (blocks[blockIndex]?.type !== 'text') {
      onBlocksChange([...blocks, { type: 'text', text: token, align: 'left', bold: false, italic: false, underline: false }]);
      return;
    }
    insertLinkInBlock(blockIndex, offsetInBlock, url, displayText);
  };

  const handleInsertImageIntoFocusedBlock = (url) => {
    const { blockIndex, offsetInBlock } = getBlockAndOffsetFromCursor(blocks, cursorPosition);
    if (blocks[blockIndex]?.type !== 'text') {
      onBlocksChange([...blocks, { type: 'text', text: `[image:${url}]`, align: 'left', bold: false, italic: false, underline: false }]);
      return;
    }
    insertImageInBlock(blockIndex, offsetInBlock, url);
  };

  const handleInsertSeparatorIntoFocusedBlock = () => {
    const { blockIndex, offsetInBlock } = getBlockAndOffsetFromCursor(blocks, cursorPosition);
    if (blocks[blockIndex]?.type !== 'text') {
      onBlocksChange([...blocks, { type: 'text', text: DIVIDER_TOKEN, align: 'left', bold: false, italic: false, underline: false }]);
      return;
    }
    insertSeparatorInBlock(blockIndex, offsetInBlock);
  };

  const textBlocks = blocks.filter(b => b.type === 'text');
  const textBlockIndices = blocks.map((b, i) => (b.type === 'text' ? i : -1)).filter(i => i >= 0);
  const blockIndexForLine = (() => {
    const idx = getBlockIndexFromCursor(blocks, cursorPosition);
    return blocks[idx]?.type === 'text' ? idx : (textBlockIndices[0] ?? (blocks.length > 0 ? 0 : -1));
  })();

  const applyToCurrentLine = (updates) => {
    if (blockIndexForLine >= 0) {
      updateBlock(blockIndexForLine, updates);
    } else if (blocks.length === 0 || textBlocks.length === 0) {
      onBlocksChange([{ type: 'text', text: '', align: 'left', bold: false, italic: false, underline: false, ...updates }]);
    }
  };

  const handleInsertImageByFile = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !onImageUpload) return;
    e.target.value = '';
    setImageUploading(true);
    try {
      const url = await onImageUpload(file);
      if (url) handleInsertImageIntoFocusedBlock(url);
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setImageUploading(false);
    }
  };

  const handleInsertLink = () => {
    const url = (linkUrlPrompt || '').trim();
    if (url && validateUrl(url)) {
      handleInsertLinkIntoFocusedBlock(url, linkTextPrompt);
      setShowLinkModal(false);
      setLinkUrlPrompt('');
      setLinkTextPrompt('');
    }
  };

  const handleInsertFacebookVideoBlock = () => {
    const normalized = normalizeFacebookVideoPageUrl(facebookVideoPrompt);
    if (!normalized) {
      setFacebookVideoError('Podaj link do reela lub filmu z Facebooka albo wklej kod osadzenia (iframe).');
      return;
    }
    onBlocksChange([...(blocks || []), { type: 'facebookVideo', url: normalized }]);
    setShowFacebookVideoModal(false);
    setFacebookVideoPrompt('');
    setFacebookVideoError('');
  };

  const removeFacebookVideoBlock = (blockIndex) => {
    onBlocksChange(blocks.filter((_, i) => i !== blockIndex));
  };

  const currentBlock = blockIndexForLine >= 0 ? blocks[blockIndexForLine] : null;
  const align = currentBlock?.align ?? 'left';
  const bold = currentBlock?.bold ?? false;
  const italic = currentBlock?.italic ?? false;
  const underline = currentBlock?.underline ?? false;

  return (
    <div>
      {/* Pasek formatowania – stosowany do bieżącej linii */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
          alignItems: 'center',
          padding: '8px 0',
          borderBottom: '1px solid #eee',
          marginBottom: '8px'
        }}
      >
        <span style={{ fontSize: '12px', color: '#666', marginRight: '8px' }}>Wyrównanie:</span>
        {[
          { id: 'left', label: 'Lewo', title: 'Do lewej' },
          { id: 'center', label: 'Środek', title: 'Do środka' },
          { id: 'right', label: 'Prawo', title: 'Do prawej' }
        ].map(({ id, label, title }) => (
          <button
            key={id}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => applyToCurrentLine({ align: id })}
            style={{
              padding: '6px 10px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: align === id ? '#e0e0e0' : '#fff',
              cursor: 'pointer',
              fontSize: '12px'
            }}
            title={title}
          >
            {label}
          </button>
        ))}
        <span style={{ fontSize: '12px', color: '#666', marginLeft: '12px', marginRight: '8px' }}>Tekst:</span>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => applyToCurrentLine({ bold: !bold })}
          style={{
            padding: '6px 10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: bold ? '#e0e0e0' : '#fff',
            fontWeight: 'bold',
            cursor: 'pointer',
            fontSize: '12px'
          }}
          title="Pogrubienie"
        >
          B
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => applyToCurrentLine({ italic: !italic })}
          style={{
            padding: '6px 10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: italic ? '#e0e0e0' : '#fff',
            fontStyle: 'italic',
            cursor: 'pointer',
            fontSize: '12px'
          }}
          title="Kursywa"
        >
          I
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => applyToCurrentLine({ underline: !underline })}
          style={{
            padding: '6px 10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: underline ? '#e0e0e0' : '#fff',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontSize: '12px'
          }}
          title="Podkreślenie"
        >
          U
        </button>
        <span style={{ fontSize: '12px', color: '#666', marginLeft: '12px', marginRight: '8px' }}>Wstaw:</span>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setShowImageModal(true)}
          disabled={imageUploading}
          style={{
            padding: '6px 10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: '#fff',
            cursor: imageUploading ? 'wait' : 'pointer',
            fontSize: '12px'
          }}
          title="Wstaw zdjęcie"
        >
          🖼 Zdjęcie
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleInsertSeparatorIntoFocusedBlock}
          style={{
            padding: '6px 10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: '#fff',
            cursor: 'pointer',
            fontSize: '12px'
          }}
          title="Wstaw separator (linia pozioma)"
        >
          ➖ Separator
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setShowLinkModal(true)}
          style={{
            padding: '6px 10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: '#fff',
            cursor: 'pointer',
            fontSize: '12px'
          }}
          title="Wstaw link"
        >
          🔗 Link
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setFacebookVideoError('');
            setShowFacebookVideoModal(true);
          }}
          style={{
            padding: '6px 10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            backgroundColor: '#fff',
            cursor: 'pointer',
            fontSize: '12px'
          }}
          title="Wstaw reel lub film z Facebooka"
        >
          📹 Reel FB
        </button>
      </div>
      {showImageModal && (
        <div style={{ marginBottom: '12px', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
          <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 'bold' }}>Wstaw zdjęcie</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {onImageUpload ? (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleInsertImageByFile}
                />
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={imageUploading} style={{ padding: '6px 12px', cursor: imageUploading ? 'wait' : 'pointer' }}>
                  {imageUploading ? 'Przesyłanie...' : 'Prześlij plik'}
                </button>
              </>
            ) : (
              <span style={{ fontSize: '12px', color: '#666' }}>Przesyłanie plików nie jest skonfigurowane.</span>
            )}
            <button type="button" onClick={() => setShowImageModal(false)} style={{ padding: '6px 12px', cursor: 'pointer' }}>Anuluj</button>
          </div>
        </div>
      )}
      {showFacebookVideoModal && (
        <div style={{ marginBottom: '12px', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
          <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 'bold' }}>Reel / film z Facebooka</div>
          <p style={{ fontSize: '12px', color: '#666', margin: '0 0 8px' }}>
            Wklej link (np. <code>facebook.com/reel/…</code>) lub cały kod osadzenia z Facebooka. Film pojawi się jako osobny blok w treści.
          </p>
          <textarea
            value={facebookVideoPrompt}
            onChange={(e) => {
              setFacebookVideoPrompt(e.target.value);
              setFacebookVideoError('');
            }}
            placeholder="https://www.facebook.com/reel/… lub kod iframe"
            rows={4}
            style={{ width: '100%', padding: '8px', marginBottom: '8px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'inherit', fontSize: '13px' }}
          />
          {facebookVideoError ? (
            <p style={{ color: '#c62828', fontSize: '12px', margin: '0 0 8px' }}>{facebookVideoError}</p>
          ) : null}
          {isFacebookVideoPageUrl(facebookVideoPrompt) ? (
            <p style={{ color: '#2e7d32', fontSize: '12px', margin: '0 0 8px' }}>Link rozpoznany — gotowy do dodania.</p>
          ) : null}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" onClick={handleInsertFacebookVideoBlock} style={{ padding: '6px 12px', cursor: 'pointer' }}>
              Dodaj blok wideo
            </button>
            <button
              type="button"
              onClick={() => {
                setShowFacebookVideoModal(false);
                setFacebookVideoPrompt('');
                setFacebookVideoError('');
              }}
              style={{ padding: '6px 12px', cursor: 'pointer' }}
            >
              Anuluj
            </button>
          </div>
        </div>
      )}
      {showLinkModal && (
        <div style={{ marginBottom: '12px', padding: '12px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
          <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 'bold' }}>Wstaw link w tekście</div>
          <input
            type="url"
            value={linkUrlPrompt}
            onChange={(e) => setLinkUrlPrompt(e.target.value)}
            placeholder="URL (https://...)"
            style={{ width: '100%', padding: '8px', marginBottom: '8px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
          />
          <input
            type="text"
            value={linkTextPrompt}
            onChange={(e) => setLinkTextPrompt(e.target.value)}
            placeholder="Tekst wyświetlany jako link (opcjonalnie – jeśli pusty, pokaże się adres URL)"
            style={{ width: '100%', padding: '8px', marginBottom: '8px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={handleInsertLink} style={{ padding: '6px 12px', cursor: 'pointer' }}>Wstaw w tekście</button>
            <button type="button" onClick={() => { setShowLinkModal(false); setLinkUrlPrompt(''); setLinkTextPrompt(''); }} style={{ padding: '6px 12px', cursor: 'pointer' }}>Anuluj</button>
          </div>
        </div>
      )}
      {/* Jedno duże białe pole – cały tekst w jednym polu, bloki niewidoczne (Enter = nowy blok) */}
      <div
        style={{
          minHeight: '280px',
          border: '1px solid #ddd',
          padding: '12px',
          backgroundColor: '#fff',
          borderRadius: '4px',
          boxSizing: 'border-box'
        }}
      >
        <textarea
          value={serializeBlocksToContent(blocks)}
          onChange={(e) => onBlocksChange(parseContentToBlocks(e.target.value))}
          onSelect={(e) => setCursorPosition(e.target.selectionStart)}
          onKeyUp={(e) => setCursorPosition(e.target.selectionStart)}
          onClick={(e) => setCursorPosition(e.target.selectionStart)}
          placeholder="Wpisz treść... (Enter = nowy, niezależny wiersz)"
          style={{ width: '100%', minHeight: '256px', padding: '8px', border: 'none', borderRadius: 0, fontFamily: 'inherit', fontSize: '14px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
        />
        {blocks.some((b) => b.type === 'facebookVideo' && b.url) && (
          <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Filmy z Facebooka w treści</div>
            {blocks.map((block, blockIndex) => {
              if (block.type !== 'facebookVideo' || !block.url) return null;
              const previewUrl = buildFacebookVideoEmbedUrl(block.url, 400);
              return (
                <div
                  key={`fb-${blockIndex}`}
                  style={{
                    marginBottom: '12px',
                    padding: '8px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <div style={{ fontSize: '11px', color: '#666', wordBreak: 'break-all', marginBottom: '8px' }}>
                    {block.url}
                  </div>
                  {previewUrl ? (
                    <iframe
                      title={`Podgląd Facebook ${blockIndex}`}
                      src={previewUrl}
                      width="100%"
                      height={getFacebookEmbedPlayerHeight(400)}
                      style={{ border: 'none', maxWidth: '400px', display: 'block', margin: '0 auto' }}
                      allow="autoplay; encrypted-media; picture-in-picture"
                    />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => removeFacebookVideoBlock(blockIndex)}
                    style={{
                      marginTop: '8px',
                      padding: '4px 10px',
                      fontSize: '11px',
                      border: '1px solid #c62828',
                      borderRadius: '4px',
                      backgroundColor: '#ffebee',
                      color: '#c62828',
                      cursor: 'pointer',
                    }}
                  >
                    Usuń film
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {/* Podgląd: zdjęcia jako obrazki ze skalowaniem i wyrównaniem */}
        {blocks.some(b => b.type === 'text' && (b.text || '').match(SEGMENT_TOKEN_RE)) && (
          <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #eee' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Podgląd zdjęć i separatorów w treści</div>
            {blocks.map((block, blockIndex) => {
              if (block.type !== 'text') return null;
              const segments = getSegments(block.text || '');
              const hasMedia = segments.some(s => s.type === 'image' || s.type === 'divider');
              if (!hasMedia) return null;
              return (
                <div key={blockIndex} style={{ marginBottom: '12px' }}>
                  {segments.map((seg, segIdx) => {
                    if (seg.type === 'divider') {
                      const removeSegment = () => {
                        const newSegments = segments.filter((_, i) => i !== segIdx);
                        updateBlock(blockIndex, { text: segmentsToText(newSegments) });
                      };
                      return (
                        <div key={segIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '8px 0' }}>
                          <hr style={{ flex: 1, border: 'none', borderTop: '1px solid #ccc', margin: 0 }} />
                          <button type="button" onClick={removeSegment} style={{ padding: '2px 6px', fontSize: '11px', border: '1px solid #c62828', borderRadius: '4px', backgroundColor: '#ffebee', color: '#c62828', cursor: 'pointer' }} title="Usuń separator">Usuń</button>
                        </div>
                      );
                    }
                    if (seg.type === 'image') {
                      const updateSegmentImage = (updates) => {
                        const newSegments = segments.map((s, i) => i === segIdx && s.type === 'image' ? { ...s, ...updates } : s);
                        updateBlock(blockIndex, { text: segmentsToText(newSegments) });
                      };
                      const removeSegmentImage = () => {
                        const newSegments = segments.filter((_, i) => i !== segIdx);
                        updateBlock(blockIndex, { text: segmentsToText(newSegments) });
                      };
                      return (
                        <div key={segIdx} style={{ display: 'flex', flexDirection: 'column', alignItems: seg.align === 'center' ? 'center' : seg.align === 'right' ? 'flex-end' : 'flex-start', width: '100%', boxSizing: 'border-box', margin: '8px 0' }}>
                          <div style={{ alignSelf: seg.align === 'center' ? 'center' : seg.align === 'right' ? 'flex-end' : 'flex-start', width: 'fit-content', maxWidth: '100%' }}>
                            <ResizableImage src={seg.url} alt="" width={seg.width || '100%'} onWidthChange={(w) => updateSegmentImage({ width: w })} />
                          </div>
                          <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap', alignItems: 'center', alignSelf: seg.align === 'center' ? 'center' : seg.align === 'right' ? 'flex-end' : 'flex-start' }}>
                            <span style={{ fontSize: '11px', color: '#666', marginRight: '4px' }}>Wyrównanie:</span>
                            {['left', 'center', 'right'].map((a) => (
                              <button key={a} type="button" onClick={() => updateSegmentImage({ align: a })} style={{ padding: '2px 6px', fontSize: '11px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: (seg.align || 'left') === a ? '#e0e0e0' : '#fff', cursor: 'pointer' }} title={a === 'left' ? 'Do lewej' : a === 'center' ? 'Do środka' : 'Do prawej'}>{a === 'left' ? 'Lewo' : a === 'center' ? 'Środek' : 'Prawo'}</button>
                            ))}
                            <button type="button" onClick={removeSegmentImage} style={{ padding: '2px 6px', fontSize: '11px', border: '1px solid #c62828', borderRadius: '4px', backgroundColor: '#ffebee', color: '#c62828', cursor: 'pointer', marginLeft: '4px' }} title="Usuń zdjęcie">Usuń</button>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

